import * as React from 'react';
import { useParams, Redirect, useLocation, matchPath } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';

import getLastProjectId from 'shared/utils/getLastProjectId';
import {
  useManagerNotificationsStore,
  useProjectStore,
  useRootStore,
  useWebsocketStore
} from 'stores/index';
import { urls } from 'shared/entities/domain';
import PageTemplate from 'shared/newComponents/PageTemplate';

const Project = React.lazy(
  () => import(/* webpackChunkName: "page-project" */ '../../Project')
);
const Settings = React.lazy(
  () => import(/* webpackChunkName: "page-settings" */ '../../Settings')
);
const News = React.lazy(
  () => import(/* webpackChunkName: "page-news" */ '../../News')
);

const ProjectLoadContainer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  const rootStore = useRootStore();
  const projectStore = useProjectStore();
  const websocketStore = useWebsocketStore();
  const managerNotificationsStore = useManagerNotificationsStore();
  const location = useLocation();

  const isScenario = matchPath(
    location.pathname,
    urls.PROJECT.tabs.SCENARIO.mask
  );

  const actualProjectId = React.useMemo(() => {
    if (projectId) {
      return projectId;
    }

    const lastProjectId = getLastProjectId({
      projects: rootStore.projectsStore.projects
    });

    return lastProjectId;
  }, [projectId, rootStore.projectsStore.projects]);

  React.useEffect(() => {
    projectStore.reset();
    if (rootStore.cabinetInitialized && actualProjectId) {
      projectStore.load(actualProjectId);
    }
  }, [actualProjectId, rootStore.cabinetInitialized]);

  const isProjectReady =
    projectStore.initialized && projectStore.projectId === actualProjectId;

  React.useEffect(() => {
    if (!isProjectReady) {
      return;
    }

    // личный канал подписывается через тот же WS-клиент, поэтому после connect
    void websocketStore.connect().then(() => managerNotificationsStore.init());
  }, [isProjectReady, websocketStore, managerNotificationsStore]);

  const content = (
    <Switch>
      <Route component={Project} path={urls.PROJECT.mask} />
      <Route component={Settings} path={urls.SETTINGS.mask} />
      <Route component={News} path={urls.NEWS.mask} />
      <Redirect to={urls.SETTINGS.root} />
    </Switch>
  );

  return isScenario ? content : <PageTemplate>{content}</PageTemplate>;
};

export default observer(ProjectLoadContainer);
